'use client';

import { useCallback, useEffect, useId, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { X } from 'lucide-react';
import { useShowcaseFrame } from './ShowcaseDeviceFrame';

const POPOVER_WIDTH = 224; // matches w-56
const VIEWPORT_MARGIN = 12;

/** A restrained clickable hotspot that reveals a small explanatory panel in place.
 * Never navigates -- it only ever discloses more context about the preview it sits on.
 *
 * The popover portals into the enclosing ShowcaseDeviceFrame's overflow-visible layer (a
 * sibling of the clipped screen) so it can render outside the rounded preview frame instead
 * of being cut off by it, while still being positioned relative to this exact button. */
export default function Hotspot({
  label,
  note,
  className = '',
}: {
  label: string;
  note: string;
  className?: string;
}) {
  const id = useId();
  const frame = useShowcaseFrame();
  const btnRef = useRef<HTMLButtonElement | null>(null);
  const [pos, setPos] = useState<{ top: number; left: number } | null>(null);

  const open = frame?.openHotspotId === id;

  const computePosition = useCallback(() => {
    const btn = btnRef.current;
    const layer = frame?.getPopoverLayer();
    if (!btn || !layer) return;

    const btnRect = btn.getBoundingClientRect();
    const layerRect = layer.getBoundingClientRect();

    // Horizontal: align the popover's left edge with the button by default; flip to align its
    // right edge with the button instead when that would run past the frame's own right edge.
    // Clamping to the frame's bounds (not just the viewport) guarantees the popover can never
    // reach into the column beside it, so it can never cover the section CTA there.
    let left = btnRect.left;
    if (left + POPOVER_WIDTH + VIEWPORT_MARGIN > layerRect.right) {
      left = btnRect.right - POPOVER_WIDTH;
    }
    left = Math.max(
      layerRect.left + VIEWPORT_MARGIN,
      Math.min(left, layerRect.right - POPOVER_WIDTH - VIEWPORT_MARGIN)
    );

    // Vertical: always place below the hotspot. ShowcaseDeviceFrame reserves extra bottom
    // margin whenever a popover is open, so there is always room and nothing below gets hidden.
    const viewportTop = btnRect.bottom + 8;

    setPos({ top: viewportTop - layerRect.top, left: left - layerRect.left });
  }, [frame]);

  function toggle() {
    if (!frame) return;
    if (open) {
      frame.setOpenHotspotId(null);
    } else {
      computePosition();
      frame.setOpenHotspotId(id);
    }
  }

  useEffect(() => {
    if (!open || !frame) return;
    computePosition();

    const onReposition = () => computePosition();
    window.addEventListener('resize', onReposition);
    window.addEventListener('scroll', onReposition, true);

    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') frame.setOpenHotspotId(null);
    };
    document.addEventListener('keydown', onKeyDown);

    return () => {
      window.removeEventListener('resize', onReposition);
      window.removeEventListener('scroll', onReposition, true);
      document.removeEventListener('keydown', onKeyDown);
    };
  }, [open, frame, computePosition]);

  return (
    <div className={`relative inline-flex ${className}`}>
      <button
        ref={btnRef}
        type="button"
        aria-expanded={open}
        onClick={toggle}
        className="inline-flex items-center gap-1 rounded-full border border-[#0f6e56]/30 bg-white/95 px-2.5 py-1 text-[11px] font-bold text-[#0f6e56] shadow-sm transition-colors duration-150 hover:bg-white focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#1d9e75] focus-visible:ring-offset-1"
      >
        {label}
      </button>
      {open &&
        frame &&
        pos &&
        (() => {
          const layer = frame.getPopoverLayer();
          if (!layer) return null;
          return createPortal(
            <div
              role="note"
              className="pointer-events-auto absolute w-56 rounded-[9px] border border-slate-200 bg-white p-3 text-left text-xs leading-5 text-slate-700 shadow-lg motion-safe:animate-fade-in"
              style={{ top: pos.top, left: pos.left }}
            >
              <button
                type="button"
                onClick={() => frame.setOpenHotspotId(null)}
                aria-label="Close"
                className="absolute right-2 top-2 text-slate-400 hover:text-slate-600 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#1d9e75] focus-visible:ring-offset-1"
              >
                <X size={13} aria-hidden="true" />
              </button>
              <p className="pr-4">{note}</p>
            </div>,
            layer
          );
        })()}
    </div>
  );
}
